import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import BaraFeatureGuard from 'components/bara/BaraFeatureGuard';
import { apiGetBaraReferrals, apiAssignBaraReferral } from 'api';
import type { APIBaraReferral, APIBaraReferralPage } from 'types/api.types';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import ReferralReviewModal from 'views/bara/ReferralReviewModal';
import {
  BARA_REFERRAL_STATUS_READY_FOR_REVIEW,
  BARA_REFERRAL_STATUS_UNDER_REVIEW,
  BARA_REFERRAL_STATUS_APPROVED,
  BARA_REFERRAL_STATUS_DECLINED,
  BARA_REFERRAL_STATUS_NEEDS_INFO,
} from 'variables/constants';

const STATUS_BADGE: Record<number, { label: string; color: string }> = {
  [BARA_REFERRAL_STATUS_READY_FOR_REVIEW]: {
    label: 'Ready for Review',
    color: 'orange',
  },
  [BARA_REFERRAL_STATUS_UNDER_REVIEW]: { label: 'Under Review', color: 'blue' },
  [BARA_REFERRAL_STATUS_APPROVED]: { label: 'Approved', color: 'green' },
  [BARA_REFERRAL_STATUS_DECLINED]: { label: 'Declined', color: 'red' },
  [BARA_REFERRAL_STATUS_NEEDS_INFO]: { label: 'Needs Info', color: 'yellow' },
};

const ReferralQueueView = () => {
  const queuePaginationEnabled = useFeatureFlag(
    'enable-bara-referral-queue-pagination',
  );
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const [searchParams, setSearchParams] = useSearchParams();
  const handledLink = useRef<string | null>(null);

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const pageSize = 25;
  const {
    data,
    isFetching: loading,
    error,
    refetch,
  } = useQuery<APIBaraReferralPage | APIBaraReferral[]>({
    queryKey: [
      'bara-referral-queue',
      queuePaginationEnabled,
      page,
      status,
      search,
    ],
    queryFn: () =>
      apiGetBaraReferrals(
        queuePaginationEnabled
          ? {
              paginated: 'true',
              page: String(page),
              page_size: String(pageSize),
              ...(status ? { status } : {}),
              ...(search ? { search } : {}),
            }
          : undefined,
      ),
    retry: false,
  });
  // Accept the legacy response during flag rollout or rollback.
  const paginationActive = queuePaginationEnabled && !Array.isArray(data);
  const referrals = Array.isArray(data) ? data : (data?.results ?? []);
  const count = Array.isArray(data) ? data.length : (data?.count ?? 0);
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  // A decision may remove the last row on the current filtered page.
  useEffect(() => {
    if (paginationActive && data && page > totalPages) setPage(totalPages);
  }, [paginationActive, data, page, totalPages]);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [reviewReferralId, setReviewReferralId] = useState<string | null>(null);

  const fetchReferrals = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Open a linked referral independently of the current page or filters.
  useEffect(() => {
    const action = searchParams.get('action');
    const referralId = searchParams.get('referral_id');
    if (!referralId || (!queuePaginationEnabled && action !== 'assign')) return;
    const linkKey = `${action}:${referralId}`;
    if (handledLink.current === linkKey) return;
    handledLink.current = linkKey;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete('action');
        next.delete('referral_id');
        return next;
      },
      { replace: true },
    );

    if (action !== 'assign') {
      setReviewReferralId(referralId);
      return;
    }

    apiAssignBaraReferral(referralId)
      .then((data: any) => {
        toastRef.current({
          title: 'Referral assigned to you',
          status: 'success',
          duration: 3000,
        });
        if (data?.jira_warning) {
          toastRef.current({
            title: data.jira_warning,
            status: 'warning',
            duration: 8000,
            isClosable: true,
          });
        }
        setReviewReferralId(referralId);
        fetchReferrals();
      })
      .catch((err: any) => {
        const msg = err?.detail || 'Failed to assign referral';
        toastRef.current({ title: msg, status: 'error', duration: 4000 });
        setReviewReferralId(referralId);
      });
  }, [searchParams, setSearchParams, fetchReferrals, queuePaginationEnabled]);

  const handleAssign = async (referralId: string) => {
    setAssigningId(referralId);
    try {
      const data: any = await apiAssignBaraReferral(referralId);
      toast({
        title: 'Referral assigned to you',
        status: 'success',
        duration: 3000,
      });
      if (data?.jira_warning) {
        toast({
          title: data.jira_warning,
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });
      }
      fetchReferrals();
      setReviewReferralId(referralId);
    } catch (err: any) {
      toast({
        title: err?.detail || 'Failed to assign — may already be assigned',
        status: 'error',
        duration: 4000,
      });
      setReviewReferralId(referralId);
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <BaraFeatureGuard>
      <Box pt={{ base: '130px', md: '80px' }} px={{ base: 4, md: 6 }}>
        <Heading size="md" mb={6}>
          Referral Queue
        </Heading>

        <Card>
          {paginationActive && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setPage(1);
                setSearch(searchDraft.trim());
              }}
            >
              <Flex mb={4} gridGap={3} flexWrap="wrap">
                <Input
                  aria-label="Search referrals"
                  placeholder="Broker, market, Jira key or full policy/referral UUID"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  flex="1"
                  minW="240px"
                  maxLength={200}
                />
                <Select
                  aria-label="Referral status"
                  w="210px"
                  value={status}
                  onChange={(event) => {
                    setStatus(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All statuses</option>
                  <option value="ready_for_review">Ready for Review</option>
                  <option value="under_review">Under Review</option>
                  <option value="needs_info">Needs Info</option>
                  <option value="approved">Approved</option>
                  <option value="declined">Declined</option>
                </Select>
                <Button type="submit" colorScheme="brand">
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchDraft('');
                    setSearch('');
                    setStatus('');
                    setPage(1);
                  }}
                >
                  Clear
                </Button>
              </Flex>
            </form>
          )}
          {error ? (
            <Box py={6} textAlign="center">
              <Text color="red.500">Failed to load referrals.</Text>
              <Button mt={2} onClick={fetchReferrals}>
                Retry
              </Button>
            </Box>
          ) : loading ? (
            <Flex justify="center" py={10}>
              <HStack>
                <Spinner size="sm" />
                <Text color="gray.500">Loading referrals…</Text>
              </HStack>
            </Flex>
          ) : referrals.length === 0 ? (
            <Text color="gray.400" py={6} textAlign="center">
              {paginationActive
                ? 'No referrals match your filters.'
                : 'No pending referrals.'}
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    {paginationActive && <Th>Policy UUID</Th>}
                    <Th>Broker</Th>
                    <Th>Market</Th>
                    <Th>Effective Date</Th>
                    <Th>Status</Th>
                    <Th>Submitted</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {referrals.map((r) => {
                    const badge = STATUS_BADGE[r.status];
                    const isOpen =
                      r.status === BARA_REFERRAL_STATUS_READY_FOR_REVIEW; // unassigned, available to claim
                    return (
                      <Tr key={r.id} _hover={{ bg: 'gray.50' }}>
                        {paginationActive && (
                          <Td fontSize="xs" whiteSpace="nowrap">
                            {r.policy_uuid}
                          </Td>
                        )}
                        <Td>
                          <Text fontSize="sm" fontWeight="medium">
                            {r.submitted_by}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">{r.market || '—'}</Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {r.effective_date ? String(r.effective_date) : '—'}
                          </Text>
                        </Td>
                        <Td>
                          {badge && (
                            <Badge
                              colorScheme={badge.color}
                              borderRadius="full"
                              px={2}
                            >
                              {badge.label}
                            </Badge>
                          )}
                        </Td>
                        <Td>
                          <Text fontSize="xs" color="gray.400">
                            {new Date(r.submitted_at).toLocaleDateString()}
                          </Text>
                        </Td>
                        <Td>
                          <HStack spacing={2} justify="flex-end">
                            {isOpen && (
                              <Button
                                size="xs"
                                colorScheme="brand"
                                isLoading={assigningId === r.id}
                                onClick={() => handleAssign(r.id)}
                              >
                                Assign to Me
                              </Button>
                            )}
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => setReviewReferralId(r.id)}
                            >
                              View
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
          {paginationActive && (
            <Flex
              mt={4}
              justify="space-between"
              align="center"
              gridGap={3}
              flexWrap="wrap"
            >
              <Text fontSize="sm" color="gray.500">
                {data
                  ? `${count} referrals · Page ${page} of ${totalPages}`
                  : 'Referrals'}
              </Text>
              <HStack>
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={loading || page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={loading || !!error || page >= totalPages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </HStack>
            </Flex>
          )}
        </Card>

        {reviewReferralId && (
          <ReferralReviewModal
            referralId={reviewReferralId}
            isOpen={!!reviewReferralId}
            onClose={() => setReviewReferralId(null)}
            onDecision={fetchReferrals}
          />
        )}
      </Box>
    </BaraFeatureGuard>
  );
};

export default ReferralQueueView;
